import React, { useEffect, useState } from "react";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import MessageBar, {
  config,
  DOBASEURL,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
  successNotify,
} from "../../utils/helper";
import Images from "../../utils/images";
import { FaUserTimes } from "react-icons/fa";
import RejectModel from "../RejectModel/RejectModel";
import { authorizedGet, authorizedPost } from "../../Config/networkWithToken";
import configuration from "../../Config/configuration";
import Strings from "../../utils/Strings";
import Spinner from "../Spinner/Spinner";

const SuggestionDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = location.state || {};
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [index, setIndex] = useState(0);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");

  const handleNoteChange = (e) => {
    setComment(e.target.value);
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const response = await authorizedGet(
        `${configuration.apis.getSuggestion}?adminId=${adminId}&userId=${userId}`,
      );

      const res = response?.data;

      if (res?.status) {
        setMatches(res?.data);
      }
    } catch (error) {
      console.log(error);
      errorNotify(Strings.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveComment = async () => {
    if (!comment.trim()) {
      setError("Please select or type a reason");
      return;
    }

    try {
      const data = {
        adminId: adminId,
        matchId: selectedMatch?.id,
        comments: comment,
        decision: "reject",
      };

      const response = await authorizedPost(
        configuration.apis.adminChangeStatus,
        data,
        config,
      );

      const res = response?.data;

      if (res?.status === false) {
        errorNotify(response.data.message || "An error occurred");
        return;
      }

      successNotify(response.data.message);
      setMatches(res?.data);
      setShowRejectModal(false);
      setComment("");
      setSelectedMatch(null);
      setError("");
    } catch (err) {
      errorNotify(err?.message || "Suggestion already rejected");
      setError("Failed to reject. Try again later.");
    }
  };

  const closeModal = () => {
    setShowRejectModal(false);
    setSelectedMatch(null);
    setComment("");
    setError("");
  };

  return (
    <div className="">
      {loading ? (
        <div className="flex min-h-screen flex-col items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="rounded-xl p-5">
          {/* Back Button */}
          <div className="flex items-center justify-between space-x-2">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-base text-gray-700 underline hover:text-gray-900 sm:text-lg"
            >
              <IoMdArrowRoundBack className="mr-2 text-base sm:text-lg" />
              <span className="font-medium">Back</span>
            </button>
          </div>

          {/* User Profile */}
          <div className="flex items-center justify-center">
            <div className="mx-auto flex max-w-[90%] flex-col items-center justify-center gap-x-6 rounded-lg border bg-white p-4 shadow-sm sm:max-w-[80%] sm:flex-row sm:items-start sm:p-6 lg:max-w-[600px]">
              <Link
                to={`/suggestion-history/suggestion-details/viewProfile/${user?._id || user?.userId}`}
              >
                <img
                  src={
                    user?.photos?.length > 0
                      ? DOBASEURL + user?.photos?.[0]
                      : user?.gender === "male"
                        ? Images.MEN
                        : Images.WOMEN
                  }
                  alt={user?.name}
                  className="aspect-square w-36 rounded-full border border-gray-300 object-cover"
                />
              </Link>

              <div className="flex w-full flex-col gap-y-2 text-center sm:text-left">
                <h2 className="text-sm font-semibold text-gray-800 sm:text-lg">
                  {user?.name?.length
                    ? user?.name?.slice(0, 18) +
                      (user?.name.length > 15 ? "..." : "")
                    : "NA"}
                </h2>
                <div className="flex justify-center gap-x-2 text-xs text-gray-600 sm:justify-start sm:text-sm">
                  <p>{user?.age} years</p>
                  <p>|</p>
                  <p>{user?.maritalStatus}</p>
                </div>
                <p className="text-xs text-gray-500 sm:text-sm">
                  {user?.primaryEmail}
                </p>
                <p className="text-xs text-gray-500 sm:text-sm">
                  {user?.phone}
                </p>

                <div className="my-3 w-full border-t border-gray-300"></div>

                <p className="text-center text-[10px] text-gray-600 sm:text-[12px]">
                  Click the profile to view the full user details.
                </p>
              </div>
            </div>
          </div>

          {/* Matched Users Section */}
          <h3 className="mt-6 text-lg font-semibold text-gray-800">
            Matched Users
          </h3>
          <div className="mt-3 grid w-full grid-cols-1 gap-3 xl:grid-cols-2">
            {matches && matches.length > 0 ? (
              matches?.map((match, index) => (
                <div
                  key={index}
                  className="relative rounded-lg border bg-white p-3 shadow-sm"
                >
                  <div className="flex min-h-[100px] flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    {/* Profile Section */}
                    <div className="flex flex-col items-center sm:flex-row sm:items-center sm:gap-4">
                      <Link
                        to={`/suggestion-history/suggestion-details/viewProfile/${match?.UserId}`}
                      >
                        <img
                          src={
                            match?.photos?.length > 0
                              ? DOBASEURL + match?.photos?.[0]
                              : match?.gender === "male"
                                ? Images.MEN
                                : Images.WOMEN
                          }
                          alt={match?.name}
                          className="h-20 w-20 rounded-full border border-gray-300 object-cover sm:h-24 sm:w-24"
                        />
                      </Link>

                      <div className="text-center sm:text-left">
                        <h4 className="text-md font-medium text-gray-800">
                          {match?.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {match?.age} years | {match?.maritalStatus}
                        </p>
                        <p className="text-xs text-gray-500 sm:text-sm">
                          {match?.phone}
                        </p>

                        {/* Status */}
                        <p className="text-sm text-gray-600">
                          Status:{" "}
                          <span
                            className={`font-medium ${
                              match?.matchedUserStatus === "accepted"
                                ? "text-green-500"
                                : match?.matchedUserStatus === "pending"
                                  ? "text-yellow-500"
                                  : match?.matchedUserStatus === "rejected"
                                    ? "text-red-500"
                                    : "text-gray-600"
                            }`}
                          >
                            {match?.matchedUserStatus}
                          </span>
                        </p>

                        {/* Rejection Reason */}
                        <div className="max-w-[220px] text-sm text-gray-600">
                          {match?.matchedUserComments?.trim() && (
                            <div className="max-w-[220px] text-sm text-gray-600">
                              <span className="font-medium">
                                Rejected Reason:
                              </span>
                              <div className="scrollbar-hide flex max-h-[50px] overflow-y-auto">
                                <span className="text-red-500">
                                  {match?.matchedUserComments}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="hidden w-[1px] self-stretch bg-gray-300 sm:block"></div>

                    {/* User Status Section */}
                    <div className="flex flex-col items-center text-right sm:w-[200px] sm:items-end">
                      <p className="text-sm font-medium text-gray-800">
                        {user?.name?.length
                          ? user?.name?.slice(0, 15) +
                            (user?.name.length > 15 ? "..." : "")
                          : "NA"}
                      </p>

                      <p className="text-sm text-gray-600">
                        Status:
                        <br />
                        <span
                          className={`block font-medium ${
                            match?.userStatus === "accepted"
                              ? "text-green-500"
                              : match?.userStatus === "pending"
                                ? "text-yellow-500"
                                : match?.userStatus === "rejected"
                                  ? "text-red-500"
                                  : "text-gray-600"
                          }`}
                        >
                          {match?.userStatus}
                        </span>
                      </p>

                      {/* User Rejection Reason */}
                      <div className="max-w-[220px] text-sm text-gray-600">
                        {match?.userComments?.trim() && (
                          <div className="max-w-[220px] text-sm text-gray-600">
                            <span className="font-medium">
                              Rejected Reason:
                            </span>
                            <div className="scrollbar-hide flex max-h-[50px] overflow-y-auto">
                              <span className="text-red-500">
                                {match?.userComments}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Horizontal Divider */}
                  <hr className="mt-4 w-full border-t border-gray-300" />

                  {/* Footer Section */}
                  <div className="flex w-full flex-row p-2">
                    <div className="flex h-full w-[90%] flex-col gap-1 text-sm text-gray-500">
                      <div className="flex h-full flex-wrap items-center justify-center gap-2 ">
                        <p>
                          <span className="font-medium">Suggested by:</span>{" "}
                          {match?.adminName}
                        </p>
                        <p>|</p>
                        <p>
                          <span className="font-medium">Suggested At:</span>{" "}
                          {new Date(match?.suggestionAt).toLocaleDateString(
                            "en-CA",
                          ) || "NA"}
                        </p>
                      </div>
                      {match?.adminDecision === "reject" && (
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <div className="flex flex-row gap-2">
                            <span className="font-medium">
                              Admin Rejected Reason:
                            </span>
                            <div className="scrollbar-hide flex max-h-[50px] overflow-y-auto">
                              <span className="text-red-500">
                                {match?.adminRejectedComments}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex w-[10%] items-center justify-center">
                      {/* Reject Icon */}
                      <FaUserTimes
                        onClick={() => {
                          setSelectedMatch(match);
                          setShowRejectModal(true);
                          setIndex(index);
                        }}
                        className=" h-6 w-6 cursor-pointer rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No matched users found.</p>
            )}
          </div>

          {showRejectModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <RejectModel
                comment={comment}
                setComment={setComment}
                handleNoteChange={handleNoteChange}
                handleSaveComment={handleSaveComment}
                closeModal={closeModal}
                error={error}
                isShowDropdown={false}
              />
            </div>
          )}
        </div>
      )}
      <MessageBar />
    </div>
  );
};

export default SuggestionDetails;
